import { ShieldCheckIcon } from "@heroicons/react/24/outline";
import { StatusCode } from "database";
import { Progress, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import { useEffect, useRef, useState } from "react";

const texts = {
    [StatusCode.FINISHED]: "Completed",
    [StatusCode.RUNNING]: "Running",
    [StatusCode.INDEXING]: "Running",
    [StatusCode.IDLE]: "Running",
    [StatusCode.CANCELLED]: "Cancelled",
    [StatusCode.INCOMPLETE]: "Incomplete",
    [StatusCode.LIMITS_EXHAUSTED]: "Limits Exhausted",
    [StatusCode.ERROR]: "Failed",
};

interface StatusIndicatorProps {
    statusCode: StatusCode;
    progress: number;
    txInQueue: number | null | undefined;
    updateOrCreation: "update" | "creation";
}

const Container = ({
    children,
    className,
}: {
    children: React.ReactNode;
    className?: string;
}) => {
    return (
        <div
            className={`flex h-10 w-32 items-center justify-center gap-1 rounded ${className ?? ""}`}
        >
            {children}
        </div>
    );
};

const Text = ({ children }: { children: React.ReactNode }) => {
    return <div className="text-base font-semibold text-text">{children}</div>;
};

const StatusText = ({ children }: { children: React.ReactNode }) => {
    return <div className="text-nowrap text-xs font-medium">{children}</div>;
};

const FinishedStatusIndicator = ({
    statusCode,
}: {
    statusCode: StatusCode;
}) => {
    return (
        <Container className="bg-labels-mint">
            <ShieldCheckIcon className="block size-5 text-primary-green xl:hidden" />
            <Text>{texts[statusCode]}</Text>
        </Container>
    );
};

const RunningStatusIndicator = ({
    progress,
    txInQueue,
    updateOrCreation,
}: Partial<StatusIndicatorProps>) => {
    const [showTxInQueue, setShowTxInQueue] = useState(false);
    const initialTxInQueue = useRef(0);

    const newText = updateOrCreation === "creation" ? "" : "new";

    useEffect(() => {
        if (txInQueue == null || txInQueue === 0) return;
        if (initialTxInQueue.current < txInQueue) {
            initialTxInQueue.current = txInQueue;
        }

        const interval = setInterval(() => {
            setShowTxInQueue((prev) => !prev);
        }, 3000);

        return () => clearInterval(interval);
    }, [txInQueue]);

    return (
        <Container className="flex-col items-center justify-center text-center">
            <StatusText>
                {showTxInQueue
                    ? `${initialTxInQueue.current} ${newText} txs found`
                    : "Processing Your Wallet"}
            </StatusText>
            <Progress
                className="text-primary-accent"
                percent={progress !== 100 ? progress : 99}
                percentPosition={{
                    align: "center",
                    type: "outer",
                }}
            />
        </Container>
    );
};

const IndexingStatusIndicator = ({
    updateOrCreation,
}: {
    updateOrCreation: StatusIndicatorProps["updateOrCreation"];
}) => {
    return (
        <Container className="flex-col">
            <StatusText>
                {updateOrCreation === "creation"
                    ? "Analyzing Wallet Size"
                    : "Scanning for new txs"}
            </StatusText>
            <Spin indicator={<LoadingOutlined spin />} />
        </Container>
    );
};

const FailedStatusIndicator = ({ statusCode }: { statusCode: StatusCode }) => {
    return (
        <Container className="bg-labels-orange">
            <ErrorRoundedIcon fontSize="small" />
            <Text>{texts[statusCode]}</Text>
        </Container>
    );
};

const StatusIndicator = ({
    statusCode,
    progress,
    txInQueue,
    updateOrCreation,
}: StatusIndicatorProps) => {
    switch (statusCode) {
        case StatusCode.FINISHED:
            return <FinishedStatusIndicator statusCode={statusCode} />;
        case StatusCode.IDLE:
            return (
                <IndexingStatusIndicator updateOrCreation={updateOrCreation} />
            );
        case StatusCode.INDEXING:
            return (
                <IndexingStatusIndicator updateOrCreation={updateOrCreation} />
            );
        case StatusCode.RUNNING:
            return (
                <RunningStatusIndicator
                    progress={progress}
                    txInQueue={txInQueue}
                    updateOrCreation={updateOrCreation}
                />
            );
        case StatusCode.LIMITS_EXHAUSTED:
            return <FailedStatusIndicator statusCode={statusCode} />;
        case StatusCode.INCOMPLETE:
            return <FailedStatusIndicator statusCode={statusCode} />;
        case StatusCode.ERROR:
            return <FailedStatusIndicator statusCode={statusCode} />;
        default:
            return null;
    }
};

export default StatusIndicator;
